/** Displays a multi-week calendar view for long-term task planning and organization. */
import { eachDayOfInterval, startOfWeek, addDays } from 'date-fns';
import { Box } from '@mui/material';
import MonthDay from '@/features/planning/components/horizon/MonthDay';
import WeekdayHeader from '@/features/planning/components/horizon/WeekdayHeader';
import { useT } from '@/i18n/useT';
import { useGCalEventsForRange } from '@/features/planning/hooks/use-gcal-events-for-range';
import { filterGCalEventsForDay } from '@/features/planning/utils/gcalHorizonUtils';

const MonthView = ({ tasks }) => {
  const t = useT();
  const today = new Date();
  const startOfView = startOfWeek(today, { weekStartsOn: 1 });
  const DAYS_TO_SHOW = 42;

  const days = eachDayOfInterval({
    start: startOfView,
    end: addDays(startOfView, DAYS_TO_SHOW - 1),
  });
  const gcalEvents = useGCalEventsForRange(days[0], days[days.length - 1]);

  return (
    <Box
      sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
    >
      <Box
        data-testid="month-scroll"
        tabIndex={0}
        role="region"
        aria-label={t('plan.monthRegion', 'Month overview')}
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
      >
        <WeekdayHeader />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            gridTemplateRows: 'repeat(6, minmax(152px, 1fr))',
            gap: '4px',
            minHeight: '100%',
            boxSizing: 'border-box',
          }}
        >
          {days.map((day) => (
            <MonthDay
              key={day.toISOString()}
              date={day}
              tasks={tasks}
              gcalEvents={filterGCalEventsForDay(gcalEvents, day)}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default MonthView;
